import { EVENT_ID } from './event.model'
import { PassSecurityLevel, RequirementsStatus } from '../shared/app.enums'

export type PASS_ID = string | number

export type passStatus = 'active' | 'inactive' | 'future' | 'expired' | 'unfulfilled' | 'workOverTime'

/** YB-2316 — one related pass of a pass, as get_passes returns it (`related_passes`). */
export interface RelatedPassSummary {
  id: number
  /** True for the primary (`creator_pass_id`), false for an extra. */
  primary: boolean
  first_name: string | null
  last_name: string | null
  category: number | null
  allowed: number | null
  limitations_end: string | null
}

export class Pass {
  id: PASS_ID
  event_id: number
  areas: Array<string>
  row: string
  seat: string
  time_limitations: string
  order_number?: string
  first_name?: string
  last_name?: string
  email?: string
  country_code?: string
  mobile?: string
  id_number?: string
  transferable: boolean
  allowed: number
  status: passStatus
  related_id: Array<number>
  limitations_start: string
  limitations_end: string
  add_info: string
  personal_id?: string = ''
  wage_agreement?: string = ''
  related_wage_agreement?: string = ''
  client_role?: number | null = NaN
  assignable_client_roles?: number[] = []
  languages?: number[] = []
  q_id_origin?: number
  service_types: number[] = []
  single_uses?: number | null = null
  default_single_uses?: number | null = null
  create_pass_max_period?: number | null = null
  secured?: PassSecurityLevel = PassSecurityLevel.STANDARD
  requirements_status?: RequirementsStatus | null = null
}

export class CreatePassInput {
  event_id: string = ''
  sub_event_id?: string = ''
  areas?: Array<string> = []
  time_limitations?: string = ''
  transferable: boolean = false
  email?: string = ''
  mobile?: string = ''
  country_code?: string = ''
  first_name?: string = ''
  last_name?: string = ''
  id_number?: string = ''
  id_card_expiration_date?: string = ''
  allowed?: number = NaN
  client_id: number = NaN
  add_info: string = ''
  related_id: number | null = NaN
  category: number = NaN
  gender?: string = ''
  client_role?: number | null = NaN
  assignable_client_roles?: number[] = []
  wage_agreement?: string = ''
  related_wage_agreement?: string = ''
  day_off?: string | null = null
  personal_id?: string = ''
  address?: string = ''
  vehicle_number?: string = ''
  company?: string = ''
  family_status?: string = ''
  date_of_birth?: string = ''
  open_once?: number = NaN
  near_open?: boolean = false
  languages?: number[] = []
  service_types: number[] = []
  role?: string = ''
  create_pass?: boolean = false
  /**
   * YB-2066 per-pass room-booking override. TRI-STATE: `null` inherits the client role's setting,
   * `true` is a per-pass grant, `false` a per-pass revocation that outranks the role. The default
   * must stay `null` — a `false` default reads as a revocation nobody asked for. It still needs an
   * initializer so `Object.keys()` picks it up for the GraphQL selection (useDefineForClassFields).
   */
  booking_enabled?: boolean | null = null
  /** The rooms this pass may book, ahead of its client role's. `null` = none of its own: the role's rooms apply. */
  booking_area_ids?: number[] | null = null
  /** YB-2150/2153 stay limit in minutes (5–1440), ahead of the client role's. `null` = none of its own: the role's applies. */
  max_stay_minutes?: number | null = null
  creator_pass_id?: number | null = NaN
  /** YB-2316 — the extra related passes beside the primary `creator_pass_id`, up to 9. */
  related_pass_ids?: number[] | null = null
  create_pass_limit?: number | null = null
  single_uses?: number | null = null
  default_single_uses?: number | null = null
  create_pass_max_period?: number | null = null
  remote_attendance?: boolean = false
  attendance_manager?: boolean = false
  secured?: PassSecurityLevel = PassSecurityLevel.LEVEL_1
  requirements_status?: RequirementsStatus | null = null
}

export class UpdatePassInput {
  add_info: string = ''
  address?: string = ''
  vehicle_number?: string = ''
  company?: string = ''
  allowed?: number = NaN
  areas?: Array<string> | string | null = null
  assignable_client_roles?: number[] = []
  attendance_manager?: boolean = false
  category: number = NaN
  client_id: number = NaN
  client_role?: number | null = NaN
  country_code?: string = ''
  create_pass?: boolean = false
  /**
   * YB-2066 per-pass room-booking override. TRI-STATE: `null` inherits the client role's setting,
   * `true` is a per-pass grant, `false` a per-pass revocation that outranks the role. The default
   * must stay `null` — a `false` default reads as a revocation nobody asked for. It still needs an
   * initializer so `Object.keys()` picks it up for the GraphQL selection (useDefineForClassFields).
   */
  booking_enabled?: boolean | null = null
  /** The rooms this pass may book, ahead of its client role's. `null` = none of its own: the role's rooms apply. */
  booking_area_ids?: number[] | null = null
  /** YB-2150/2153 stay limit in minutes (5–1440), ahead of the client role's. `null` = none of its own: the role's applies. */
  max_stay_minutes?: number | null = null
  create_pass_limit?: number | null = null
  create_pass_max_period?: number | null = null
  creator_country_code?: string | null = null
  creator_first_name?: string | null = null
  creator_last_name?: string | null = null
  creator_mobile?: string | null = null
  creator_pass_id?: number | null = NaN
  /** YB-2316 — the extra related passes beside the primary `creator_pass_id`, up to 9. */
  related_pass_ids?: number[] | null = null
  /**
   * YB-2316 — the primary related first, then each extra, as get_passes returns them. No initializer:
   * the keys of this class are the GET_PASSES selection and the update payload, and an object field
   * can be in neither (GET_PASSES selects it by hand).
   */
  related_passes?: RelatedPassSummary[] | null
  date_of_birth?: string | null = null
  day_off?: string | null = null
  default_single_uses?: number | null = null
  email: string = ''
  event_id: EVENT_ID = NaN
  family_status?: string = ''
  first_name?: string = ''
  gender?: string = ''
  id: number = NaN
  id_number?: string | null = null
  id_card_expiration_date?: string | null = null
  languages?: number[] = []
  last_name?: string = ''
  limitations_end: string = ''
  limitations_start: string = ''
  mobile?: string = ''
  open_once?: boolean = false
  near_open?: boolean = false
  order_number?: string = ''
  personal_id?: string = ''
  q_id?: number = NaN
  q_id_mobile?: string = ''
  q_id_name?: number = NaN
  q_id_origin_mobile?: string = ''
  q_id_origin_name?: number = NaN
  related_id: number | null = NaN
  related_wage_agreement?: string = ''
  remote_attendance?: boolean = false
  role?: string = ''
  secured?: PassSecurityLevel = PassSecurityLevel.LEVEL_1
  service_types: number[] = []
  single_uses?: number | null = null
  status: passStatus | null = null
  sub_event_id: EVENT_ID = NaN
  sub_event_name: string = ''
  sub_sub_event_id: EVENT_ID = NaN
  sub_sub_event_name: string = ''
  time_limitations?: string = ''
  transferable?: boolean = false
  wage_agreement?: string = ''
  requirements_status?: RequirementsStatus | null = null
}
